import { useCallback, useEffect, useState } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

// Plug-and-play voice guide integration.
// Mirrors the production BibleVoice contract:
//   - GET /api/voice/ready -> { ready: boolean }
//   - /voice-call.js exposes window.bibleVoiceCall() (xAI Grok realtime voice over WS /api/voice/stream)
//
// IMPORTANT: /voice-call.js only exists on a backend that has a voice key configured.
// We therefore load it ONLY when /api/voice/ready reports true. Otherwise a static host
// returns index.html (HTML) for the missing script, which the browser would try to parse
// as JS and throw "Unexpected token '<'". Gating on readiness avoids that entirely.
export function useVoiceGuide() {
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    const loadScript = () => {
      if (typeof window === "undefined") return;
      if (typeof window.bibleVoiceCall === "function") {
        setLoaded(true);
        return;
      }
      if (document.getElementById("bv-voice-call")) return;
      const s = document.createElement("script");
      s.id = "bv-voice-call";
      s.src = `${BASE}/voice-call.js`;
      s.async = true;
      s.onload = () => active && setLoaded(true);
      s.onerror = () => active && setLoaded(false);
      document.body.appendChild(s);
    };

    fetch(`${BASE}/api/voice/ready`)
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        const isReady = !!(d && d.ready);
        setReady(isReady);
        if (isReady) loadScript();
      })
      .catch(() => active && setReady(false));

    return () => {
      active = false;
    };
  }, []);

  // Returns true if the live voice agent was launched, false if unavailable here.
  const startCall = useCallback(() => {
    if (typeof window !== "undefined" && typeof window.bibleVoiceCall === "function") {
      window.bibleVoiceCall();
      return true;
    }
    return false;
  }, []);

  return { ready, loaded, startCall };
}
